import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, FileText } from "lucide-react";

export default function AthletePlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bootstrap único no mount — intencional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await api.get("/my-plan");
        setPlan(data);
        if (data?.available) {
          // Marca como visto para limpar o badge de "novo" no painel
          api.post("/my-plan/viewed").catch((err) => {
            console.warn("[plan] falha ao marcar como visto:", err);
          });
        }
      } catch (err) {
        console.error("[plan] falha ao carregar /my-plan:", err);
        setError("Não foi possível carregar seu plano.");
      } finally { setLoading(false); }
    })();
  }, []);

  const pdfUrl = plan?.available && plan?.athlete_id
    ? `${API}/athletes/${plan.athlete_id}/plan/download`
    : null;
  // Nota: usamos Authorization no fetch para download via link — como <iframe> não envia headers,
  // baixamos via fetch autenticado e criamos um blob URL para exibir.
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false; let created = null;
    (async () => {
      if (!plan?.available || !plan?.athlete_id) return;
      try {
        const res = await api.get(`/athletes/${plan.athlete_id}/plan/download`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        created = url;
        if (!cancelled) setBlobUrl(url);
      } catch (err) {
        console.error("[plan] falha ao baixar PDF do plano:", err);
        if (!cancelled) setError("Não foi possível exibir o PDF do plano.");
      }
    })();
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [plan]);

  return (
    <AppLayout kind="athlete">
      <h1 className="text-3xl sm:text-4xl text-white font-semibold mb-2">Plano de Treinamento</h1>
      <p className="text-slate-400 mb-8">Documento oficial preparado pelos treinadores GSX.</p>

      {loading && <div className="text-slate-400 py-10 text-center" data-testid="plan-loading">Carregando…</div>}
      {error && !loading && <div className="text-red-300 py-4">{error}</div>}

      {!loading && !error && !plan?.available && (
        <Card className="bg-transparent gsx-card-dark p-10 text-center" data-testid="plan-empty">
          <FileText className="w-10 h-10 mx-auto text-amber-400/70" />
          <div className="text-white font-semibold mt-4">Seu plano ainda não está disponível</div>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
            Assim que o seu treinador publicar o Plano de Treinamento em PDF ele aparecerá nesta área.
          </p>
        </Card>
      )}

      {!loading && !error && plan?.available && (
        <Card className="bg-transparent gsx-card-dark p-6" data-testid="plan-content">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold">Plano vigente</div>
              <div className="text-white font-semibold mt-1">{plan.name}</div>
              {plan.uploaded_at && (
                <div className="text-xs text-slate-400 mt-1">Publicado em {new Date(plan.uploaded_at).toLocaleDateString("pt-BR")}</div>
              )}
            </div>
            {blobUrl && (
              <a href={blobUrl} download={plan.name} data-testid="plan-download">
                <Button className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                  <FileDown className="w-4 h-4 mr-2" /> Baixar PDF
                </Button>
              </a>
            )}
          </div>
          <div className="rounded-lg overflow-hidden border border-amber-500/20">
            {blobUrl ? (
              <iframe src={blobUrl} title="Plano de Treinamento" className="w-full h-[720px] bg-white" />
            ) : (
              <div className="h-[400px] flex items-center justify-center text-slate-400">Preparando visualização…</div>
            )}
          </div>
        </Card>
      )}
    </AppLayout>
  );
}
